import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { doctors } from '../data/mockData';
import { MessageSquare, Clock, Star, Shield } from 'lucide-react';

const DoctorCard = ({ doctor }) => {
  return (
    <div className="group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100">
      {/* Image Container with Gradient Overlay */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={doctor.image}
          alt={doctor.name}
          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Availability Badge */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Online
          </span>
        </div>

        {/* Doctor Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h4 className="text-xl font-bold text-white mb-1">
            {doctor.name}
          </h4>
          <p className="text-green-300 text-sm font-medium">{doctor.specialty}</p>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-5">
        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {doctor.description || "Providing expert AI-powered healthcare guidance with evidence-based recommendations."}
        </p>

        {/* Stats Row */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1.5 text-gray-500">
            <Clock className="w-4 h-4" />
            <span>{doctor.experience || "10+ years"}</span>
          </div>
          <div className="flex items-center gap-1.5 text-amber-500">
            <Star className="w-4 h-4 fill-current" />
            <span>4.9</span>
          </div>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-2 mb-5">
          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
            <Shield className="w-3 h-3" />
            AHPRA/HIPAA Compliant
          </span>
          <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
            24/7 Available
          </span>
        </div>

        {/* Action Button */}
        <Link to={`/chat/${encodeURIComponent(doctor.name)}`} className="block">
          <Button 
            className="w-full bg-[#166534] hover:bg-[#14532d] text-white py-5 rounded-xl font-medium transition-all duration-300 group-hover:shadow-lg flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Start Consultation
          </Button>
        </Link>
      </div>
    </div>
  );
};

const DoctorsSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full text-[#166534] text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-[#166534] rounded-full animate-pulse" />
            AI Virtual Doctors
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Meet Our Expert Team
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Our AI-powered doctors are available 24/7 to provide evidence-based healthcare guidance using the latest medical research.
          </p>
        </div>

        {/* General & Primary Care */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-[#166534] rounded-full" />
            <h3 className="text-2xl font-bold text-gray-900">General & Primary Care</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {doctors.generalCare.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </div>

        {/* Lifestyle & Preventive */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-[#166534] rounded-full" />
            <h3 className="text-2xl font-bold text-gray-900">Lifestyle & Preventive</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {doctors.lifestyle.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </div>

        {/* Specialists */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-[#166534] rounded-full" />
            <h3 className="text-2xl font-bold text-gray-900">Specialists</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {doctors.specialists.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </div>

        {/* View All CTA */}
        <div className="text-center">
          <Link to="/team">
            <Button className="bg-gray-900 hover:bg-gray-800 text-white px-10 py-6 text-base font-medium rounded-xl transition-all duration-300 hover:shadow-xl">
              View All Doctors
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DoctorsSection;
